import { useEffect, useRef, useState } from "react";
import { useLang } from "./i18n";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export const useSeo = (title, desc) => {
  const { lang } = useLang();
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = lang;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", window.location.href);
    setMeta("property", "og:image", `${window.location.origin}/site/og-image.jpg`);
    setMeta("property", "og:locale", lang === "en" ? "en_GB" : "it_IT");
    setMeta("name", "twitter:card", "summary_large_image");
  }, [title, desc, lang]);
};

export const useInView = (opts = { threshold: 0.15 }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (!("IntersectionObserver" in window)) { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, opts);
    io.observe(el);
    return () => io.disconnect();
  }, [seen]); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, seen];
};

export const Reveal = ({ as: Tag = "div", className = "", delay = 0, children, ...rest }) => {
  const [ref, seen] = useInView();
  return (
    <Tag ref={ref} className={`site-reveal ${seen ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>{children}</Tag>
  );
};

const NUM = /^([^\d]*)(\d+(?:[.,]\d+)?)(.*)$/;

export const Counter = ({ value, testid }) => {
  const [ref, seen] = useInView({ threshold: 0.4 });
  const m = NUM.exec(value || "");
  const [n, setN] = useState(0);
  const target = m ? parseFloat(m[2].replace(",", ".")) : 0;
  const decimals = m && /[.,]/.test(m[2]) ? m[2].split(/[.,]/)[1].length : 0;
  useEffect(() => {
    if (!m || !seen) return;
    let raf, t0;
    const step = (t) => {
      t0 = t0 || t;
      const p = Math.min(1, (t - t0) / 1400);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [seen, target]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = m ? `${m[1]}${n.toFixed(decimals).replace(".", m[2].includes(",") ? "," : ".")}${m[3]}` : value;
  return <span ref={ref} data-testid={testid}>{shown}</span>;
};

export const CoreMark = ({ className = "" }) => (
  <span className={`font-display font-black tracking-tight text-[#0A1128] ${className}`}>core<span className="text-[#E8173A]">.</span></span>
);

export const Eyebrow = ({ children, light }) => (
  <div className={`text-xs font-bold uppercase tracking-[0.22em] mb-4 ${light ? "text-[#FF5A72]" : "text-[#E8173A]"}`}>{children}</div>
);
